import React, { useState, useRef, useEffect } from 'react'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import Icon from '@forwardflow/ui/Icons'
import { Avatar, message } from 'antd'
import { CallSummaryExpandItem } from './CallSummaryExpandItem'
import { UserAvatar } from '@forwardflow/ui'
import { getAvatarColor } from '@forwardflow/helpers/utils'
import { AudioPlayer } from './AudioPlayer'
import { useGetRecordingPresignedUrl } from '@forwardflow/hooks/customHooks/conversation/useGetRecordingPresignedUrl'
import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'

dayjs.extend(duration)

interface CallSummaryLeadItem {
   item: any
   selectedLeadDetails: any
}

export const CallSummaryLeadItem: React.FC<CallSummaryLeadItem> = ({
   item,
   selectedLeadDetails,
}) => {
   const { data: user } = useAuthUser({ revalidateOnMount: true })
   const [isExpanded, setIsExpanded] = useState(false)
   const [isSummaryExpanded, setIsSummaryExpanded] = useState(false)
   const [audioUrl, setAudioUrl] = useState(item?.recordingUrl || '')
   const [needsPresignedUrl, setNeedsPresignedUrl] = useState(false)
   const [isLoading, setIsLoading] = useState(false)
   const attemptPlayRef = useRef(false)
   const audioRef = useRef<HTMLAudioElement>(null)

   const recordingFileObjKey = item?.recordingFileObjKey

   const { data, mutate } = useGetRecordingPresignedUrl(
      needsPresignedUrl ? recordingFileObjKey : undefined
   )

   useEffect(() => {
      if (data?.url) {
         setAudioUrl(data.url)
         setIsLoading(false)

         if (attemptPlayRef.current && audioRef.current) {
            setTimeout(() => {
               if (audioRef.current) {
                  audioRef.current.play().catch((err) => {
                     console.error('Retry play failed:', err)
                  })
               }
            }, 100)
         }
      }
   }, [data])

   useEffect(() => {
      const audio = audioRef.current
      if (!audio) return

      const handlePlay = () => {
         attemptPlayRef.current = true
      }

      const handlePause = () => {
         attemptPlayRef.current = false
      }

      audio.addEventListener('play', handlePlay)
      audio.addEventListener('pause', handlePause)

      return () => {
         audio.removeEventListener('play', handlePlay)
         audio.removeEventListener('pause', handlePause)
      }
   }, [])

   const handleAudioError = () => {
      console.log('Audio playback error - fetching presigned URL')
      if (!needsPresignedUrl) {
         setIsLoading(true)
         setNeedsPresignedUrl(true)
         mutate()
      }
   }

   const toggleExpand = () => {
      setIsSummaryExpanded(!isSummaryExpanded)
   }
   const handleCopy = () => {
      navigator.clipboard.writeText(item?.stepId).then(() => {
         message.success('Copied to clipboard')
      })
   }

   return (
      <div className="flex justify-start">
         <div className="flex flex-col items-end justify-end overflow-hidden gap-x-2 rounded-3xl rounded-bl max-w-[800px] 3xl:w-[800px]">
            <div className="flex gap-x-3 bg-[#594180] py-4 px-8 w-full gap-y-4 relative group items-center">
               <div className="flex flex-col items-start flex-1 w-full">
                  {user?.email === 'operator@forward-flow.com' &&
                     item?.stepId && (
                        <div className="text-[11px] text-white/70 px-0 pb-3 flex gap-x-4">
                           <span
                              className="cursor-pointer select-all"
                              title="Click to copy"
                              onClick={handleCopy}
                           >
                              Step Id:{' '}
                              <span className="text-[12px] font-medium">
                                 {item?.stepId}
                              </span>
                           </span>
                           {item?.campaignPhase && (
                              <span>
                                 Phase:{' '}
                                 <span className="text-[12px] font-medium">
                                    {item?.campaignPhase}
                                 </span>
                              </span>
                           )}
                           {item?.campaignType && (
                              <span>
                                 Type:{' '}
                                 <span className="text-[12px] font-medium">
                                    {item?.campaignType}
                                 </span>
                              </span>
                           )}
                        </div>
                     )}
                  <div className="flex w-full">
                     <div className="flex items-center">
                        <div>
                           <UserAvatar
                              backgroundColor={getAvatarColor(
                                 selectedLeadDetails?.stageInfo?.name
                              )}
                              className="!w-10 !h-10 !text-[14px]"
                           >
                              {selectedLeadDetails?.userInfo?.firstName.slice(
                                 0,
                                 1
                              ) || 'L'}
                           </UserAvatar>
                        </div>
                        <div className="-ml-3">
                           <Avatar
                              src={'/images/ff_white.png'}
                              className="w-10 h-10 p-1 bg-primary-dark/60"
                           >
                              {item?.assistant?.name.slice(0, 1) || 'A'}
                           </Avatar>
                        </div>
                     </div>
                     {audioUrl && (
                        <div
                           className={`w-full h-full px-4 m-auto flexc flex-1`}
                        >
                           <AudioPlayer
                              audioRef={audioRef}
                              audioSrc={audioUrl}
                              onError={handleAudioError}
                              isLoading={isLoading}
                           />
                        </div>
                     )}
                  </div>
               </div>
               <div className="h-10 px-4 py-2 rounded-full flexc gap-x-3 bg-black/20 shrink-0">
                  <Icon name="Call" className="fill-white" />
                  <p className="text-[14px]">
                     {dayjs
                        .duration(item?.callDuration, 'seconds')
                        .format('m[m] s[s]')}
                  </p>
               </div>
            </div>

            {item?.data && (
               <div
                  onClick={toggleExpand}
                  className="flex items-center justify-between w-full px-8 py-5 pb-5 border-b gap-x-5 border-white/20 bg-white/10"
               >
                  <div
                     className={`${
                        isSummaryExpanded ? '' : 'line-clamp-1'
                     } transition-all duration-200 text-[14px]`}
                  >
                     Call summary : {item?.data}
                  </div>
                  <Icon
                     name="arrow-down"
                     className={`${
                        isSummaryExpanded ? '-rotate-180' : ''
                     } text-white/50`}
                  />
               </div>
            )}

            {!isExpanded && (
               <div
                  className="flex flex-col items-center justify-between w-full gap-3 px-8 py-5 cursor-pointer bg-white/10"
                  onClick={() => setIsExpanded(!isExpanded)}
               >
                  <div className="flex items-center w-full gap-x-3">
                     <div className="flex items-center justify-between gap-x-3">
                        {item?.callEvents?.map((data: any, index: any) => {
                           return (
                              <div key={index}>
                                 <div>
                                    <p className="text-[14px] font-medium pr-4">
                                       {data?.status}
                                    </p>
                                    <div className="flex items-center gap-x-[6px]">
                                       <p className="text-[10px] font-medium text-white/70">
                                          {dayjs(data?.date).format('h:mm A')}
                                       </p>
                                       {index !==
                                          item?.callEvents?.length - 1 && (
                                          <p className="bg-white/20 h-[1px] flex-1"></p>
                                       )}
                                    </div>
                                 </div>
                              </div>
                           )
                        })}
                     </div>
                     <Icon
                        name="arrow-down"
                        className="ml-auto text-white/50"
                     />
                  </div>
               </div>
            )}

            {isExpanded && (
               <CallSummaryExpandItem
                  id={item?.id}
                  selectedLeadDetails={selectedLeadDetails}
                  setIsExpanded={setIsExpanded}
                  item={item}
               />
            )}
         </div>
      </div>
   )
}
